import type {
  BankTxStatus,
  CreateExpenseCategoryInput,
  CreateExpenseInput,
  CreateExpensePaymentInput,
  CreateSupplierInput,
  ExpenseBankImportDecision,
  ExpenseBankImportPreview,
  ExpenseBankImportResult,
  ExpenseKind,
  ExpenseSource,
  ExtractedExpenseData,
  PaymentStatus,
  SmartBillImportPreview,
  SmartBillImportResult,
  SpvExpenseSyncResult,
  UpdateExpenseCategoryInput,
  UpdateExpenseInput,
} from '@financiar/shared';
import { apiClient } from '../../api/client';
import type { Paginated } from '../../api/types';

// --- Response shapes (hand-written, same convention as FacturiPage) ---------

export interface SupplierItem {
  id: string;
  name: string;
  cui: string | null;
  countryCode: string;
  iban: string | null;
  isActive: boolean;
}

export interface ExpenseCategoryItem {
  id: string;
  name: string;
  isActive: boolean;
}

export interface ExpenseListItem {
  id: string;
  kind: ExpenseKind;
  source: ExpenseSource;
  docNumber: string | null;
  docDate: string;
  dueDate: string | null;
  currency: string;
  netAmount: string | null;
  vatAmount: string | null;
  totalAmount: string;
  /** Valoarea în RON (pentru valută: total × curs BNR la data documentului) —
   * baza tuturor totalurilor. */
  totalAmountRon: string;
  /** Cursul BNR folosit (RON per 1 unitate); null pentru RON. */
  exchangeRate: string | null;
  paidAmountRon: string;
  paymentStatus: PaymentStatus;
  settled: boolean;
  description: string | null;
  notes: string | null;
  supplier: { id: string; name: string } | null;
  category: { id: string; name: string } | null;
}

export interface ExpenseAttachmentMeta {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface ExpensePaymentItem {
  id: string;
  bankTransactionId: string | null;
  paidOn: string;
  amountRon: string;
  notes: string | null;
  bankTransaction: {
    id: string;
    transactionDate: string;
    description: string | null;
    statementSource: string;
    amount: string;
  } | null;
}

export interface ExpenseDetail extends ExpenseListItem {
  supplier: { id: string; name: string; cui: string | null; countryCode: string } | null;
  attachments: ExpenseAttachmentMeta[];
  payments: ExpensePaymentItem[];
}

export interface ExpensesTotals {
  totalRon: string;
  paidRon: string;
  unpaidRon: string;
  foreign: { currency: string; total: string }[];
}

export interface CheltuieliPage extends Paginated<ExpenseListItem> {
  totals: ExpensesTotals;
}

export interface ExpenseBankTx {
  id: string;
  transactionDate: string;
  amount: string;
  status: BankTxStatus;
  counterpartyName: string | null;
  counterpartyCif: string | null;
  description: string | null;
  statementSource: string;
  allocatedRon: string;
  remainingRon: string;
  allocations: {
    id: string;
    amountRon: string;
    expense: {
      id: string;
      kind: ExpenseKind;
      docNumber: string | null;
      description: string | null;
      supplier: { name: string } | null;
    };
  }[];
}

export interface ListCheltuieliParams {
  companyId: string;
  page: number;
  pageSize: number;
  search?: string;
  kind?: ExpenseKind;
  source?: ExpenseSource;
  supplierId?: string;
  categoryId?: string;
  paymentStatus?: PaymentStatus;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'docDate' | 'supplier' | 'total' | 'paymentStatus';
  sortOrder?: 'asc' | 'desc';
}

// --- Expenses ---------------------------------------------------------------

export async function fetchCheltuieli(params: ListCheltuieliParams): Promise<CheltuieliPage> {
  const { data } = await apiClient.get<CheltuieliPage>('/expenses', { params });
  return data;
}

export async function fetchCheltuiala(id: string): Promise<ExpenseDetail> {
  const { data } = await apiClient.get<ExpenseDetail>(`/expenses/${id}`);
  return data;
}

export async function createCheltuiala(input: CreateExpenseInput): Promise<ExpenseDetail> {
  const { data } = await apiClient.post<ExpenseDetail>('/expenses', input);
  return data;
}

export async function updateCheltuiala(id: string, input: UpdateExpenseInput): Promise<ExpenseDetail> {
  const { data } = await apiClient.patch<ExpenseDetail>(`/expenses/${id}`, input);
  return data;
}

export async function deleteCheltuiala(id: string): Promise<void> {
  await apiClient.delete(`/expenses/${id}`);
}

export async function markCheltuialaPaid(id: string): Promise<ExpenseDetail> {
  const { data } = await apiClient.post<ExpenseDetail>(`/expenses/${id}/mark-paid`, {});
  return data;
}

export async function markCheltuialaUnpaid(id: string): Promise<ExpenseDetail> {
  const { data } = await apiClient.post<ExpenseDetail>(`/expenses/${id}/mark-unpaid`, {});
  return data;
}

/** Bulk-assign (or clear, with null) a category on the selected expenses. */
export async function bulkSetExpenseCategory(input: {
  companyId: string;
  expenseIds: string[];
  categoryId: string | null;
}): Promise<{ updated: number }> {
  const { data } = await apiClient.post<{ updated: number }>('/expenses/bulk-category', input);
  return data;
}

/** Both allocation endpoints return the RECOMPUTED expense. */
export async function addCheltuialaPayment(id: string, input: CreateExpensePaymentInput): Promise<ExpenseDetail> {
  const { data } = await apiClient.post<ExpenseDetail>(`/expenses/${id}/payments`, input);
  return data;
}

export async function deleteCheltuialaPayment(id: string, paymentId: string): Promise<ExpenseDetail> {
  const { data } = await apiClient.delete<ExpenseDetail>(`/expenses/${id}/payments/${paymentId}`);
  return data;
}

// --- Attachments ------------------------------------------------------------

export async function uploadCheltuialaAttachment(id: string, file: File): Promise<ExpenseAttachmentMeta> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<ExpenseAttachmentMeta>(`/expenses/${id}/attachments`, formData);
  return data;
}

export async function deleteCheltuialaAttachment(id: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/expenses/${id}/attachments/${attachmentId}`);
}

/** Plain URL — served inline with cookie auth, like `facturaPdfUrl`. */
export function attachmentUrl(expenseId: string, attachmentId: string): string {
  return `/api/v1/expenses/${expenseId}/attachments/${attachmentId}`;
}

/** Plain URL — the browser downloads the official ANAF PDF rendered from the
 * expense's e-Factura XML attachment (ANAF's public XML→PDF transform). */
export function expenseEfacturaPdfUrl(expenseId: string): string {
  return `/api/v1/expenses/${expenseId}/efactura-pdf`;
}

// --- Reconciliation workspace ----------------------------------------------

export async function fetchExpenseBankTransactions(params: {
  companyId: string;
  page: number;
  pageSize: number;
  status?: BankTxStatus;
  /** Matches counterparty name, description and counterparty CIF. */
  search?: string;
  bankAccountId?: string;
  dateFrom?: string;
  dateTo?: string;
}): Promise<Paginated<ExpenseBankTx>> {
  const { data } = await apiClient.get<Paginated<ExpenseBankTx>>('/expenses/bank-transactions', { params });
  return data;
}

export async function ignoreBankTransaction(txId: string): Promise<void> {
  await apiClient.post(`/expenses/bank-transactions/${txId}/ignore`, {});
}

export async function unignoreBankTransaction(txId: string): Promise<void> {
  await apiClient.post(`/expenses/bank-transactions/${txId}/unignore`, {});
}

// --- Suppliers & categories -------------------------------------------------

export async function fetchSuppliers(params: {
  companyId: string;
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<Paginated<SupplierItem>> {
  const { data } = await apiClient.get<Paginated<SupplierItem>>('/suppliers', {
    params: { page: 1, pageSize: 20, ...params },
  });
  return data;
}

export async function createSupplier(input: CreateSupplierInput): Promise<SupplierItem> {
  const { data } = await apiClient.post<SupplierItem>('/suppliers', input);
  return data;
}

export async function fetchExpenseCategories(companyId: string, includeInactive = false): Promise<ExpenseCategoryItem[]> {
  const { data } = await apiClient.get<ExpenseCategoryItem[]>('/expense-categories', {
    params: { companyId, ...(includeInactive ? { includeInactive: 'true' } : {}) },
  });
  return data;
}

export async function createExpenseCategory(input: CreateExpenseCategoryInput): Promise<ExpenseCategoryItem> {
  const { data } = await apiClient.post<ExpenseCategoryItem>('/expense-categories', input);
  return data;
}

export async function updateExpenseCategory(id: string, input: UpdateExpenseCategoryInput): Promise<ExpenseCategoryItem> {
  const { data } = await apiClient.patch<ExpenseCategoryItem>(`/expense-categories/${id}`, input);
  return data;
}

export async function deleteExpenseCategory(id: string): Promise<void> {
  await apiClient.delete(`/expense-categories/${id}`);
}

// --- AI extraction ----------------------------------------------------------

export async function fetchExtractionStatus(): Promise<{ enabled: boolean }> {
  const { data } = await apiClient.get<{ enabled: boolean }>('/expense-extraction/status');
  return data;
}

export async function extractExpenseData(file: File): Promise<ExtractedExpenseData> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<ExtractedExpenseData>('/expense-extraction', formData);
  return data;
}

// --- Imports ----------------------------------------------------------------

/** Debit-side statement import — same firm-from-file contract as the credit
 * import in features/facturi/api.ts. */
export async function previewExpenseBankImport(files: File[]): Promise<ExpenseBankImportPreview> {
  const formData = new FormData();
  for (const file of files) formData.append('files', file);
  const { data } = await apiClient.post<ExpenseBankImportPreview>('/expenses/import/bank/preview', formData);
  return data;
}

export async function commitExpenseBankImport(
  files: File[],
  decisions: Record<string, ExpenseBankImportDecision>,
): Promise<ExpenseBankImportResult> {
  const formData = new FormData();
  for (const file of files) formData.append('files', file);
  formData.append('decisions', JSON.stringify(decisions));
  const { data } = await apiClient.post<ExpenseBankImportResult>('/expenses/import/bank/commit', formData);
  return data;
}

/** SmartBill is the one import that CANNOT read the firm from the file —
 * `companyId` travels as an explicit form field. */
export async function previewSmartbillImport(companyId: string, file: File): Promise<SmartBillImportPreview> {
  const formData = new FormData();
  formData.append('companyId', companyId);
  formData.append('file', file);
  const { data } = await apiClient.post<SmartBillImportPreview>('/expenses/import/smartbill/preview', formData);
  return data;
}

export async function commitSmartbillImport(
  companyId: string,
  file: File,
  options: { markAllPaid?: boolean } = {},
): Promise<SmartBillImportResult> {
  const formData = new FormData();
  formData.append('companyId', companyId);
  formData.append('file', file);
  if (options.markAllPaid) formData.append('markAllPaid', 'true');
  const { data } = await apiClient.post<SmartBillImportResult>('/expenses/import/smartbill/commit', formData);
  return data;
}

export async function syncSpvReceived(companyId: string, days = 60): Promise<SpvExpenseSyncResult> {
  const { data } = await apiClient.post<SpvExpenseSyncResult>(`/firms/${companyId}/expenses/spv-sync`, undefined, {
    params: { days },
  });
  return data;
}
